import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Form, Input, Modal, Typography } from 'antd';
import moment from 'moment';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';

import { useSendErrorReportMutation } from '@app/api/ErrorReports';
import { Phone, UploadFilesComponent } from '@app/components';
import type { PhoneValidation } from '@app/components/Phone/types';
import {
  CANCEL,
  DATE_FORMATS,
  PREFIX,
  RULES_IS_REQUIRE,
  SAVE,
  SUCCESS,
  BUG_REPORT_MODAL_FILES_EXTENSIONS,
} from '@app/constants';
import { APP_VERSION, APP_VERSION_TIMESTAMP } from '@app/data/version';
import { useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { handleCatchError } from '@app/services';
import { APP_VERSION_TIMEZONE_OFFSET } from '@app/shared/utils';
import { selectUser, useAppSelector } from '@app/store';
import { clearSessionStorageAndReload, getBrowserInfo } from '@app/utils';

import type {
  CreateBugReportModalApi,
  ModalFormProps,
} from '@features/BugReport/components/CreateBugReportModal/types';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import type { UploadFile } from 'antd';

import '@app/features/BugReport/style/BugReport.desktop.scss';

const { Text } = Typography;
const { TextArea } = Input;
const { useForm, useWatch } = Form;

const NO_EMPTY_SPACE_RULE = {
  whitespace: true,
  message: 'Поле не может принимать только пробелы',
};

export const CreateBugReportModal = forwardRef<CreateBugReportModalApi>(
  (_, ref) => {
    const [isOpen, toggleVisibility] = useState(false);

    const [form] = useForm<ModalFormProps>();

    const { nameFirst, nameLast } = useAppSelector(selectUser);

    const files = useWatch('files', form);
    const { showNotification } = useNotification();
    const { activeBranchId, user } = useUserPrint();
    const [phoneValidation, setPhoneValidation] = useState<PhoneValidation>({
      isValid: false,
      errorMessage: null,
    });

    const [sendBugReport, { isLoading }] = useSendErrorReportMutation();

    useEffect(() => {
      form.setFieldValue('UserName', `${nameLast || ''} ${nameFirst || ''}`);
    }, [form, nameFirst, nameLast]);

    useImperativeHandle(ref, () => ({
      open: () => {
        toggleVisibility(true);
      },
    }));

    const handleCancel = () => {
      toggleVisibility(false);
      form.resetFields();
    };

    const handlePaste = (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
      const data = event.clipboardData.items;
      for (let i = 0; i < data.length; i++) {
        const item = data[i];
        if (item.kind === 'file') {
          const blob = item.getAsFile();
          if (blob) {
            const timeStamp = moment().format('hh-mm-ss');
            const file = new File([blob], `${timeStamp}-${blob.name}`, {
              type: blob.type,
            });
            const modelFile = {
              lastModified: 1724383909217,
              lastModifiedDate:
                'Fri Aug 23 2024 06:31:49 GMT+0300 (Москва, стандартное время)',
              name: `${timeStamp}-${blob.name}`,
              originFileObj: file,
              percent: 100,
              response: true,
              size: 238229,
              status: 'done',
              type: 'image/png',
              uid: moment().format('DDMMYYYYhhmmss'),
              xhr: undefined,
            };
            files
              ? form.setFieldValue('files', [...files, modelFile])
              : [modelFile];
          }
        }
      }
    };

    const handleFormSave = async (values: ModalFormProps) => {
      const {
        Message,
        Browser,
        UserName,
        Phone: { Phone },
      } = values;
      const { errorMessage, isValid } = phoneValidation;
      const phoneErrorMessage =
        errorMessage || (!isValid ? 'Обязательное поле' : '');

      if (phoneErrorMessage) {
        form.setFields([
          {
            name: 'Phone',
            errors: [phoneErrorMessage],
          },
        ]);
        return;
      }

      const formData = new FormData();

      if (files && files.length > 0) {
        files.forEach((file: UploadFile) => {
          const originFile = file.originFileObj;

          if (originFile) {
            formData.append('attachments', originFile, originFile.name);
          }
        });
      }

      try {
        await sendBugReport({
          UserName,
          UserPhone: Phone,
          Browser,
          Message,
          ActiveBranchId: activeBranchId,
          data: formData,
        }).unwrap();

        showNotification('Сообщение об ошибке отправлено успешно', SUCCESS);

        handleCancel();
      } catch (error) {
        showNotification(handleCatchError(error));
      }
    };

    const systemVersionTimestamp = APP_VERSION_TIMESTAMP
      ? moment
          .unix(APP_VERSION_TIMESTAMP)
          .utcOffset(APP_VERSION_TIMEZONE_OFFSET)
          .format(DATE_FORMATS.ISO_FORMAT_NO_TIMEZONE)
      : null;

    const handleOkButtonClick = () => {
      form.submit();
    };

    useEffect(() => {
      if (!isOpen) {
        form.resetFields();
        return;
      }
      form.setFieldValue('Phone', {
        Phone: user?.phone,
        CountryId: 0,
      });
    }, [isOpen, user, form]);

    return (
      <Modal
        centered
        open={isOpen}
        onOk={handleOkButtonClick}
        okText={SAVE}
        cancelText={CANCEL}
        onCancel={handleCancel}
        title="Сообщение об ошибке"
        className={`${PREFIX}create-bug-report-modal`}
        okButtonProps={{
          loading: isLoading,
        }}
        cancelButtonProps={{
          disabled: isLoading,
        }}
      >
        <Form
          form={form}
          initialValues={{
            Message: '',
            files: [],
            Browser: getBrowserInfo(),
            UserName: `${nameLast || ''} ${nameFirst || ''}`,
          }}
          onFinish={handleFormSave}
        >
          <div className="container">
            <div>
              <FontAwesomeIcon
                icon={'fa-solid fa-circle-exclamation' as IconProp}
                size="lg"
                className="info-icon"
                style={{ color: 'red' }}
              />{' '}
              <Text>
                Внимание! Перед отправкой ошибки, очистите кэш браузера нажав
                кнопку ниже и затем перепроверьте наличие проблемы.
              </Text>
            </div>
            <Button
              type="primary"
              onClick={() => clearSessionStorageAndReload()}
            >
              Очистить кэш браузера
            </Button>
            <Text>
              Если после этого ошибка сохраняется, пожалуйста, напишите нам об
              этом в форме ниже.
            </Text>
            <div>
              <FontAwesomeIcon
                icon={'fa-solid fa-circle-info' as IconProp}
                size="lg"
                className="info-icon"
              />{' '}
              Опишите подробно возникшую ошибку и приложите скриншот экрана.
              Укажите, при каких обстоятельствах возникла ошибка.
            </div>
            {APP_VERSION ? (
              <Text>{`Ваша версия системы: ${APP_VERSION} ${
                systemVersionTimestamp
                  ? `от ${systemVersionTimestamp} (МСК)`
                  : ''
              }`}</Text>
            ) : null}

            <div className="item-container">
              <Form.Item
                name="Message"
                className="message-form-item"
                rules={[...RULES_IS_REQUIRE, NO_EMPTY_SPACE_RULE]}
              >
                <TextArea
                  className="send-message-text-area"
                  autoSize={{ minRows: 5, maxRows: 5 }}
                  placeholder="Введите сообщение для отправки"
                  maxLength={750}
                  onPaste={handlePaste}
                />
              </Form.Item>
            </div>
            <div className="item-container">
              <UploadFilesComponent
                formItemName={'files'}
                uploadFilesExtensions={BUG_REPORT_MODAL_FILES_EXTENSIONS}
              />
            </div>
            <div className="item-container">
              <Text strong className="label label-require">
                Браузер, в котором произошла ошибка
              </Text>
              <Form.Item
                name="Browser"
                className="message-form-item"
                rules={[...RULES_IS_REQUIRE, NO_EMPTY_SPACE_RULE]}
              >
                <Input size="large" />
              </Form.Item>
            </div>
            <div>
              <FontAwesomeIcon
                icon={'fa-solid fa-circle-info' as IconProp}
                size="lg"
                className="info-icon"
              />{' '}
              Укажите контактные данные для связи.
            </div>
            <div className="item-container">
              <Text strong className="label label-require">
                Имя пользователя
              </Text>
              <Form.Item
                name="UserName"
                className="message-form-item"
                rules={[...RULES_IS_REQUIRE, NO_EMPTY_SPACE_RULE]}
              >
                <Input size="large" />
              </Form.Item>
            </div>
            {isOpen && (
              <div className="item-container">
                <Text strong className="label label-require">
                  Телефон пользователя
                </Text>
                <Form.Item name="Phone" className="message-form-item">
                  <Phone
                    name="Phone"
                    required
                    onValidate={setPhoneValidation}
                    disabled={!isOpen}
                  />
                </Form.Item>
              </div>
            )}
          </div>
        </Form>
      </Modal>
    );
  }
);
